<template>
  <v-dialog v-model="internalValue" max-width="420">
    <v-card>
      <div class="pa-4 qr-modal-card" :style="backgroundStyle">
        <v-img 
          :src="waitPaymentSrc" 
          class="wait-payment-overlay"
          contain
        ></v-img>
        <v-btn icon small class="qr-close-btn" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <div class="payment-info-wrapper" style="position: relative;">
          <h3 class="font-weight-bold mb-2">Thanh toán phiếu thu</h3>
          <div class="mb-1">Ngân hàng: <strong><span :class="{ 'loading-dots': loading && !displayBankName }">{{ displayBankName || '...' }}</span></strong></div>
          <div class="mb-1">Tên người nhận: <strong><span :class="{ 'loading-dots': loading && !displayReceiverName }">{{ displayReceiverName || '...' }}</span></strong></div>
          <div class="mb-1">Số tài khoản: <strong><span :class="{ 'loading-dots': loading && !displayAccountNumber }">{{ displayAccountNumber || '...' }}</span></strong></div>
          <div class="mb-1">Số tiền: <strong><span :class="{ 'loading-dots': loading && !amount }">{{ amount ? formatCurrencyNoD(amount) + ' (đ)' : '...' }}</span></strong></div>
          <div class="mb-1">Mã phiếu: <strong><span :class="{ 'loading-dots': loading && !code }">{{ code || '...' }}</span></strong></div>
          <div class="mb-4 text-caption grey--text text--darken-1" style="font-style: italic;">
            Quý khách vui lòng kiểm tra đúng thông tin chuyển khoản trước khi xác nhận.
          </div>
          <v-img 
            :src="companySealSrc" 
            class="company-seal-overlay"
            contain
          ></v-img>
        </div>
        <div class="d-flex justify-center mb-4">
          <div class="qr-container" style="position: relative;">
            <div class="qr-sub-logo d-flex justify-space-between mb-2" v-if="!loading && qrImageUrl">
              <v-img 
                :src="onepayLogoSrc" 
                class="payment-logo" 
                max-width="80"
                contain
              ></v-img>
              <v-img 
                :src="vietQrLogoSrc" 
                class="payment-logo" 
                max-width="80"
                contain
              ></v-img>
            </div>
            <div class="qr-wrapper" style="position: relative;">
              <div 
                v-if="loading" 
                class="qr-loading-overlay"
              >
                <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
              </div>
              <div
                v-if="!loading && fetchError"
                class="qr-error-overlay"
              >
                <div class="qr-error-content">
                  <div class="qr-error-text">Có lỗi khi tạo QR code. Thử lại sau ít phút</div>
                  <v-btn
                    small
                    text
                    class="qr-retry-btn"
                    @click="retryFetchQRCode"
                  >
                    <v-icon left small>mdi-reload</v-icon>
                    Tải lại
                  </v-btn>
                </div>
              </div>
              <v-img 
                v-if="!loading && qrImageUrl" 
                :src="qrImageUrl" 
                width="260" 
                height="260" 
                contain
              ></v-img>
              <v-img 
                v-if="!loading && qrImageUrl" 
                :src="logoSrc" 
                class="qr-logo-overlay" 
                contain
              ></v-img>
            </div>
          </div>
        </div>
        <div class="text-caption grey--text text--darken-1 mb-2">
          Quét mã QR bằng ứng dụng ngân hàng để điền nhanh nội dung và số tiền.
        </div>
        <div 
          class="text-right"
          v-if="qrImageUrl"
        >
          <v-btn small class="mr-2 qr-copy-button" color="#0075ff" dark @click="copyPng">Sao chép ảnh QR</v-btn>
          <v-btn small text color="#b70727" @click="redoQRCode">Tạo lại</v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import PaymentQRCodeRepository from '@/repositories/PaymentQRCodeRepository'

export default {
  name: 'QrPaymentModal',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    code: {
      type: String,
      default: '',
    },
    amount: {
      type: [Number, String],
      default: 0,
    },
    receiverName: {
      type: String,
      default: '',
    },
    receiptId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      copyLoading: false,
      backgroundImageUrl: null,
      loading: false,
      qrImageUrl: null,
      fetchError: false,
      bankName: '',
      accountNumber: '',
      accountHolderName: '',
    }
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    qrUrl() {
      return this.qrImageUrl;
    },
    logoSrc() {
      return require('@/assets/logo2.png');
    },
    companySealSrc() {
      return require('@/assets/company-seal.png');
    },
    waitPaymentSrc() {
      return require('@/assets/wait-payment.png');
    },
    onepayLogoSrc() {
      return require('@/assets/onepay_logo.png');
    },
    vietQrLogoSrc() {
      return require('@/assets/vietqr_logo.png');
    },

    backgroundStyle() {
      if (this.backgroundImageUrl) {
        return {
          backgroundImage: `url(${this.backgroundImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        };
      }
      return {};
    },
    showPaymentInfo() {
      return !!this.qrImageUrl && !this.loading && !this.fetchError;
    },
    displayReceiverName() {
      return this.accountHolderName || this.receiverName || '';
    },
    displayBankName() {
      return this.bankName || '';
    },
    displayAccountNumber() {
      return this.accountNumber || '';
    },
  },
  mounted() {
    this.generateChaoticBackground();
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.generateChaoticBackground();
        this.fetchQRCode();
      } else {
        this.resetQRData();
      }
    },
  },
  methods: {
    async fetchQRCode(options = {}) {
      const { redo } = options;
      if (!this.receiptId || !this.amount) {
        console.warn('Code hoặc amount không được để trống');
        return;
      }

      this.loading = true;
      this.fetchError = false;
      try {
        const params = {
          receipt_id: this.receiptId,
        };
        const query = redo ? { redo } : undefined;

        const response = await PaymentQRCodeRepository.generatePaymentQRCode(params, query);
        
        // Kiểm tra response từ axios
        if (response.data && response.data.isSuccess && response.data.data) {
          const data = response.data.data;
          this.bankName = (data.accounts && data.accounts.bank_name) || '';
          this.accountNumber = (data.accounts && data.accounts.account_number) || '';
          this.accountHolderName = data.name || '';
          
          // Tạo QR code từ data string sử dụng qrcode.js từ CDN
          if (data.accounts && data.accounts.qr && data.accounts.qr.data) {
            const qrData = data.accounts.qr.data;
            // Generate QR code từ data string (QRCode được load từ CDN)
            this.qrImageUrl = await window.QRCode.toDataURL(qrData, {
              width: 260,
              margin: 1,
              color: {
                dark: '#000000',
                light: '#FFFFFF'
              }
            });
            this.fetchError = false;
          } else {
            this.qrImageUrl = null;
            this.fetchError = true;
          }

        } else {
          this.qrImageUrl = null;
          this.fetchError = true;
        }
      } catch (error) {
        console.error('Error fetching QR code:', error);
        this.qrImageUrl = null;
        this.fetchError = true;
      } finally {
        this.loading = false;
      }
    },
    redoQRCode() {
      this.fetchQRCode({ redo: 1 });
    },
    retryFetchQRCode() {
      this.fetchQRCode();
    },
    resetQRData() {
      this.qrImageUrl = null;
      this.fetchError = false;
      this.bankName = '';
      this.accountNumber = '';
      this.accountHolderName = '';
    },
    close() {
      this.$emit('input', false);
    },
    async copyPng() {
      try {
        this.copyLoading = true;
        // Kích thước cơ bản
        const width = 720; // px
        const qrSize = 520;
        const headerTop = 64;
        const leftPad = 60;
        const lineGap = 50;
        const topGap = 16; // khoảng cách giữa info và QR
        const footerGap = 24; // khoảng cách giữa QR và chú thích
        const bottomPad = 48; // lề dưới

        const infoLines = [];

        if (this.showPaymentInfo && this.displayBankName) {
          infoLines.push({ label: 'Ngân hàng', value: this.displayBankName });
        }

        if (this.showPaymentInfo && this.displayReceiverName) {
          infoLines.push({ label: 'Tên người nhận', value: this.displayReceiverName });
        }

        if (this.showPaymentInfo && this.displayAccountNumber) {
          infoLines.push({ label: 'Số tài khoản', value: this.displayAccountNumber });
        }

        infoLines.push({ label: 'Số tiền', value: `${this.formatCurrencyNoD(this.amount)}(đ)` });
        infoLines.push({ label: 'Mã phiếu', value: this.code || '' });

        const infoBottomY = 120 + (infoLines.length - 1) * lineGap;
        const noteGap = 50; // khoảng cách giữa mã phiếu và ghi chú (bằng với lineGap)
        const noteY = infoBottomY + noteGap;
        // Tính chiều cao ghi chú dựa trên số dòng (ước tính tạm thời, sẽ tính lại sau khi wrap)
        const maxWidth = width - leftPad * 2;
        const tempCtx = document.createElement('canvas').getContext('2d');
        tempCtx.font = 'italic 22px Arial';
        const noteText = 'Quý khách vui lòng kiểm tra đúng thông tin chuyển khoản trước khi xác nhận.';
        const noteLines = this.wrapText(tempCtx, noteText, maxWidth);
        const noteHeight = noteLines.length * 28; // 28px cho mỗi dòng
        const logoHeight = 45; // chiều cao logo (tăng từ 35)
        const logoGapFromQR = 16; // khoảng cách từ logo đến QR
        const dynamicHeight = Math.max(
          720,
          noteY + noteHeight + topGap + logoHeight + logoGapFromQR + qrSize + footerGap + bottomPad
        );

        // Tạo canvas với chiều cao động
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = dynamicHeight;
        const ctx = canvas.getContext('2d');

        // Vẽ gradient background và wave pattern
        this.drawChaoticPattern(ctx, width, dynamicHeight);

        // Header text
        ctx.fillStyle = '#111111';
        ctx.font = 'bold 36px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Thanh toán phiếu thu', width / 2, headerTop);

        // Info text
        ctx.textAlign = 'left';
        let y = 120;
        infoLines.forEach((line, index) => {
          const labelText = `${line.label}: `;
          ctx.font = '500 28px Arial';
          ctx.fillText(labelText, leftPad, y);
          const labelWidth = ctx.measureText(labelText).width;
          ctx.font = 'bold 28px Arial';
          ctx.fillText(line.value, leftPad + labelWidth, y);
          if (index < infoLines.length - 1) {
            y += lineGap;
          }
        });

        // Vẽ ghi chú sau mã phiếu (wrap text nếu quá dài)
        ctx.fillStyle = '#666666';
        ctx.font = 'italic 22px Arial';
        ctx.textAlign = 'left';
        // Sử dụng lại noteLines đã tính trước đó
        let currentNoteY = noteY;
        noteLines.forEach((line) => {
          ctx.fillText(line, leftPad, currentNoteY);
          currentNoteY += 28; // Khoảng cách giữa các dòng ghi chú
        });

        // Vẽ logo onepay và vietqr ở trên QR code
        const onepayLogoImg = await this.loadImage(this.onepayLogoSrc);
        const vietQrLogoImg = await this.loadImage(this.vietQrLogoSrc);
        
        // Vị trí QR: đặt ngay dưới phần ghi chú với khoảng cách nhỏ
        const qrImg = await this.loadImage(this.qrUrl);
        const qrX = (width - qrSize) / 2;
        const qrY = Math.max(160, currentNoteY + 16 + logoHeight + 16); // Thêm khoảng cách cho logo
        ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
        
        // Khung chứa 2 logo có chiều rộng bằng khung QR code (qrSize)
        const logosContainerWidth = qrSize;
        const logosContainerX = qrX; // Căn giữa giống như QR code
        const logosY = qrY - logoHeight - 16; // 16px khoảng cách từ logo đến QR
        
        // Tính kích thước logo dựa trên tỷ lệ
        const onepayLogoWidth = (onepayLogoImg.width / onepayLogoImg.height) * logoHeight;
        const vietQrLogoWidth = (vietQrLogoImg.width / vietQrLogoImg.height) * logoHeight;
        
        // Vẽ 2 logo trong khung, justify-space-between (onepay bên trái, vietqr bên phải)
        // Logo onepay ở bên trái của khung
        ctx.drawImage(onepayLogoImg, logosContainerX, logosY, onepayLogoWidth, logoHeight);
        // Logo vietqr ở bên phải của khung
        ctx.drawImage(vietQrLogoImg, logosContainerX + logosContainerWidth - vietQrLogoWidth, logosY, vietQrLogoWidth, logoHeight);

        // Load logo và overlay căn giữa QR
        const logoImg = await this.loadImage(this.logoSrc);
        const logoSize = 150;
        const logoX = qrX + qrSize / 2 - logoSize / 2;
        const logoY = qrY + qrSize / 2 - logoSize / 2;
        ctx.globalAlpha = 0.9;
        ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
        ctx.globalAlpha = 1;

        // Footer note ngay dưới QR
        ctx.fillStyle = '#666666';
        ctx.textAlign = 'center';
        ctx.font = '400 20px Arial';
        const footerTextY = Math.min(dynamicHeight - 36, qrY + qrSize + footerGap);
        ctx.fillText('Quét bằng ứng dụng ngân hàng để điền nhanh nội dung và số tiền', width / 2, footerTextY);

        // Vẽ ảnh company-seal (tương tự vị trí trong modal: top: 10%, left: 40%)
        const companySealImg = await this.loadImage(this.companySealSrc);
        const sealSize = 380;
        const sealX = width * 0.4;
        const sealY = dynamicHeight * 0.07;
        ctx.save();
        ctx.globalAlpha = 0.7;
        ctx.drawImage(companySealImg, sealX, sealY, sealSize, sealSize);
        ctx.restore();

        // Vẽ ảnh wait-pay ở góc trên bên trái (rotate -45deg)
        const waitPayImg = await this.loadImage(this.waitPaymentSrc);
        const waitPaySize = 240;
        const waitPayX = -40;
        const waitPayY = -40;
        ctx.save();
        ctx.globalAlpha = 0.6;
        ctx.translate(waitPayX + waitPaySize / 2, waitPayY + waitPaySize / 2);
        ctx.rotate(-45 * Math.PI / 180);
        ctx.drawImage(waitPayImg, -waitPaySize / 2, -waitPaySize / 2, waitPaySize, waitPaySize);
        ctx.restore();

        // Vẽ watermark pattern chống chỉnh sửa
        // this.drawWavePattern(ctx, width, dynamicHeight);

        // Convert to blob and copy
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        if (blob && navigator.clipboard && window.ClipboardItem) {
          await navigator.clipboard.write([
            new window.ClipboardItem({ 'image/png': blob })
          ]);
          this.pushCopyNotification({
            type: 'info',
            message: 'Đã sao chép ảnh QR.'
          });
        } else {
          // Fallback: open in new tab if clipboard unsupported
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => URL.revokeObjectURL(url), 5000);
          this.pushCopyNotification({
            type: 'info',
            message: 'Ảnh QR đã được mở trong tab mới.'
          });
        }
      } catch (e) {
        console.error('Copy PNG failed', e);
        this.pushCopyNotification({
          type: 'error',
          message: 'Không thể sao chép ảnh QR. Vui lòng thử lại.'
        });
      } finally {
        this.copyLoading = false;
      }
    },
    loadImage(src) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = typeof src === 'string' ? src : '';
      });
    },
    drawWavePattern(ctx, width, height) {
      // Lưu trạng thái canvas hiện tại
      ctx.save();
      
      // Thiết lập blend mode và độ mờ cho pattern
      ctx.globalAlpha = 0.15;
      ctx.globalCompositeOperation = 'multiply';
      
      // Màu pattern (xám nhạt, không quá nổi)
      ctx.strokeStyle = '#999999';
      ctx.lineWidth = 1.5;
      
      // Vẽ pattern sóng ngang dọc (wave grid) làm watermark chống chỉnh sửa
      const waveSpacing = 40;
      const waveAmplitude = 8;
      
      // Sóng ngang
      for (let y = 0; y < height; y += waveSpacing) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        for (let x = 0; x < width; x += 2) {
          const waveY = y + Math.sin((x / waveSpacing) * Math.PI * 2 + (y / 50)) * waveAmplitude;
          ctx.lineTo(x, waveY);
        }
        ctx.stroke();
      }
      
      // Sóng dọc
      for (let x = 0; x < width; x += waveSpacing) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        for (let y = 0; y < height; y += 2) {
          const waveX = x + Math.cos((y / waveSpacing) * Math.PI * 2 + (x / 50)) * waveAmplitude;
          ctx.lineTo(waveX, y);
        }
        ctx.stroke();
      }
      
      // Khôi phục trạng thái canvas
      ctx.restore();
    },
    formatCurrencyNoD(val) {
      const cv = parseInt(val);
      const res = cv.toLocaleString('it-IT', { style: 'currency', currency: 'VND' }) || '';
      return res.slice(0, -3);
    },
    pushCopyNotification({ type = 'info', message = '' }) {
      if (!this.$store || !message) return;
      this.$store.commit('ADD_APP_NOTIFICATION', {
        type,
        title: type === 'error' ? 'Sao chép thất bại' : 'Sao chép ảnh QR',
        message,
        code: this.code ? String(this.code) : '',
        receiptId: this.receiptId || '',
      });
    },
    generateChaoticBackground() {
      const canvas = document.createElement('canvas');
      canvas.width = 800;
      canvas.height = 800;
      const ctx = canvas.getContext('2d');
      
      // Vẽ gradient background
      this.drawGradientBackground(ctx, canvas.width, canvas.height);
      
      // Vẽ wave pattern
      this.drawWavePatternOnBackground(ctx, canvas.width, canvas.height);
      
      // Convert to data URL
      this.backgroundImageUrl = canvas.toDataURL('image/png');
    },
    drawGradientBackground(ctx, width, height) {
      // Vẽ gradient background từ góc trên bên trái xuống góc dưới bên phải
      // Gradient chạy chéo: màu đậm ở góc trên trái, trắng ở góc dưới phải
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#FFE4C4'); // Màu cam/da đậm hơn ở góc trên trái
      gradient.addColorStop(1, '#FFFFFF'); // Màu trắng ở góc dưới phải
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    },
    drawWavePatternOnBackground(ctx, width, height) {
      ctx.save();
      
      // Màu cho wave pattern (giữ màu cam và xanh nhưng với opacity phù hợp)
      const color1 = 'rgba(255, 104, 10, 0.15)'; // #ff680a
      const color2 = 'rgba(43, 168, 226, 0.15)'; // #2ba8e2
      
      // Wave pattern layer 1
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = color1;
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = 1;
      
      const waveSpacing = 50;
      const waveAmplitude = 6;
      
      // Sóng ngang với màu cam
      for (let y = 0; y < height; y += waveSpacing) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        for (let x = 0; x < width; x += 2) {
          const waveY = y + Math.sin((x / waveSpacing) * Math.PI * 2 + (y / 50)) * waveAmplitude;
          ctx.lineTo(x, waveY);
        }
        ctx.stroke();
      }
      
      // Sóng dọc với màu xanh
      ctx.strokeStyle = color2;
      for (let x = 0; x < width; x += waveSpacing) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        for (let y = 0; y < height; y += 2) {
          const waveX = x + Math.cos((y / waveSpacing) * Math.PI * 2 + (x / 50)) * waveAmplitude;
          ctx.lineTo(waveX, y);
        }
        ctx.stroke();
      }
      
      // Wave pattern layer 2
      ctx.globalAlpha = 0.08;
      ctx.strokeStyle = color1;
      const waveSpacing2 = 80;
      const waveAmplitude2 = 4;
      
      // Sóng ngang layer 2
      for (let y = 0; y < height; y += waveSpacing2) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        for (let x = 0; x < width; x += 2) {
          const waveY = y + Math.sin((x / waveSpacing2) * Math.PI * 2 + (y / 80)) * waveAmplitude2;
          ctx.lineTo(x, waveY);
        }
        ctx.stroke();
      }
      
      // Sóng dọc layer 2
      ctx.strokeStyle = color2;
      for (let x = 0; x < width; x += waveSpacing2) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        for (let y = 0; y < height; y += 2) {
          const waveX = x + Math.cos((y / waveSpacing2) * Math.PI * 2 + (x / 80)) * waveAmplitude2;
          ctx.lineTo(waveX, y);
        }
        ctx.stroke();
      }
      
      ctx.restore();
    },
    drawChaoticPattern(ctx, width, height, color1, color2) {
      // Vẽ gradient background
      this.drawGradientBackground(ctx, width, height);
      
      // Vẽ wave pattern
      this.drawWavePatternOnBackground(ctx, width, height);
    },
    wrapText(ctx, text, maxWidth) {
      const words = text.split(' ');
      const lines = [];
      let currentLine = words[0];

      for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const width = ctx.measureText(currentLine + ' ' + word).width;
        if (width < maxWidth) {
          currentLine += ' ' + word;
        } else {
          lines.push(currentLine);
          currentLine = word;
        }
      }
      lines.push(currentLine);
      return lines;
    },
  },
};
</script>

<style scoped>
.qr-modal-card {
  background-color: #FFFFFF;
  position: relative;
}

.qr-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.qr-sub-logo {
  width: 100%;
}

.qr-wrapper {
  position: relative;
  width: 260px;
  height: 260px;
}
.payment-logo {
  max-height: 20px;
  object-fit: contain;
}
.qr-close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  background-color: rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
}
.qr-close-btn .v-icon {
  color: rgba(0, 0, 0, 0.87);
  transition: color 0.3s ease;
}
.qr-close-btn:hover {
  background-color: rgba(0, 0, 0, 0.87) !important;
}
.qr-close-btn:hover .v-icon {
  color: rgba(255, 255, 255, 0.8) !important;
}
.qr-logo-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 75px;
  height: 75px;
  opacity: 0.9;
  pointer-events: none;
}
.qr-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}
.qr-error-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  z-index: 2;
  background-color: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(6px);
}
.qr-error-content {
  text-align: center;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 8px;
}
.qr-error-text {
  font-weight: 600;
  color: #b70727;
  font-size: 14px; 
}
.qr-retry-btn {
  color: #0075ff;
  background-color: transparent;
  transition: background-color 0.2s ease, color 0.2s ease;
}
.qr-retry-btn:hover {
  background-color: #0075ff;
  color: #ffffff;
}
.qr-copy-button {
  transition: all 0.3s ease;
}
.qr-copy-button:hover {
  background-color: transparent !important;
  border: 1px solid #0075ff !important;
  color: #0075ff !important;
}
.qr-copy-button:hover .v-icon {
  color: #0075ff !important;
}
.loading-dots {
  display: inline-block;
  animation: blink 1.4s infinite;
}
@keyframes blink {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}
.payment-info-wrapper {
  position: relative;
}
.company-seal-overlay {
  position: absolute;
  top: 10%;
  left: 40%;
  /* transform: translate(-50%, -50%); */
  width: 190px;
  height: 190px;
  opacity: 0.7;
  pointer-events: none;
  z-index: 1;
}
.wait-payment-overlay {
  position: absolute;
  top: -20px;
  left: -20px;
  width: 120px;
  height: 120px;
  transform: rotate(-45deg);
  opacity: 0.6;
  pointer-events: none;
  z-index: 1;
}
</style>


